import { useCallback, useEffect, useState } from 'react';

import { faCircleNotch, faPlusCircle, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PaginationState } from '@tanstack/react-table';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import ChronologyTableToolbar from './TableTimeline/ChronologyTableToolbar';
import { ChronologyTimeline } from './TableTimeline/ChronologyTimeline';
import { VisualTimeline } from './VisualTimeline/VisualTimeline';
import { DownloadFormat, StyleFormat, DocumentDownloadOptions } from '../../../../../../../../backend/src/types/types';
import { TableWrapper } from '../../components/TableWrapper';
import ChronologyEventAdder from '../components/ChronologyFactAdder';
import { PageName, TrackingEvents } from '@/analytics/events';
import { trackCustomEvent, trackPageView } from '@/analytics/Mixpanel';
import { useDeleteChronologyFactWithSync } from '@/api/mutations/useDeleteChronologyFactWithSync';
import useChronologyDownload from '@/api/mutations/useDownloadChronology';
import { useGetChronology } from '@/api/queries/useGetChronology';
import { useGetChronologyFacts } from '@/api/queries/useGetChronologyFacts';
import Button from '@/components/atoms/Button';
import { DownloadModal } from '@/components/molecules/DownloadModal';
import { ChronologyExportProgressModal } from '@/components/molecules/ChronologyExportProgressModal';
import Modal from '@/components/molecules/Modals/SettingsModal';

export enum ViewMode {
  Table = 'table',
  Visual = 'visual',
}

const ChronologyEditor = () => {
  // State
  const [searchParams] = useSearchParams();
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [downloadModalIsOpen, setDownloadModalIsOpen] = useState(false);
  const [exportProgressModalIsOpen, setExportProgressModalIsOpen] = useState(false);
  const [isExportCreating, setIsExportCreating] = useState(false);
  const [exportError, setExportError] = useState<string>();
  const [viewMode, setViewMode] = useState<ViewMode>(ViewMode.Table);

  const page = searchParams.get('page');
  const chronologyId = searchParams.get('chronologyId') || '';
  const caseId = searchParams.get('caseId');

  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: page ? Number(page) - 1 : 0,
    pageSize: 50,
  });

  // Hooks
  const location = useLocation();
  const navigate = useNavigate();

  // ----- Data fetching -----
  const {
    data: responseChronologyFacts,
    isLoading: isLoadingChronologyFacts,
    refetch: refetchCaseChronologyFacts,
  } = useGetChronologyFacts({
    chronologyId,
    caseId: caseId ?? '',
    pageIndex,
    pageSize,
    paginated: viewMode === ViewMode.Table,
  });

  const { data: responseChronology } = useGetChronology({
    chronologyId,
    caseId: caseId ?? '',
  });

  const { downloadMutation, downloadWithDocuments } = useChronologyDownload({
    chronologyId,
    caseId,
  });

  const { mutateAsync: fetchDownload, isLoading: isLoadingDownload } = downloadMutation;

  // Handlers
  const handleBack = useCallback(() => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete('chronologyId');
    searchParams.delete('page');
    navigate('/app/chronos/case-editor/data/chronology?' + searchParams.toString(), { replace: true });
  }, [location.search, navigate]);

  const openModal = useCallback(() => setModalIsOpen(true), []);
  const closeModal = useCallback(() => setModalIsOpen(false), []);

  const handleNewFactCreated = () => {
    trackCustomEvent(TrackingEvents.AddFactToChronologyOnChronologyPage, { chronologyId, caseId });
    refetchCaseChronologyFacts();
    closeModal();
  };

  const handleDownloadCSV = useCallback(
    async ({
      title,
      columns,
      downloadFormat,
      includeSourceDocuments,
    }: {
      title: string;
      columns: string[];
      downloadFormat: DownloadFormat;
      includeSourceDocuments?: boolean;
    }) => {
      if (downloadFormat === DownloadFormat.EXCEL) {
        trackCustomEvent(TrackingEvents.DownloadChronologyTableCSV, { chronologyId, caseId });
      } else if (downloadFormat === DownloadFormat.DOCX) {
        trackCustomEvent(TrackingEvents.DownloadChronologyTableDocx, { chronologyId, caseId });
      }

      if (includeSourceDocuments) {
        trackCustomEvent(TrackingEvents.DownloadChronologyWithDocuments, { chronologyId, caseId });

        try {
          const result = await fetchDownload({
            title,
            columns,
            styleFormat: StyleFormat.Table,
            downloadFormat,
            includeSourceDocuments,
          });

          if (result) {
            setDownloadModalIsOpen(false);

            if (result.exportExists) {
              // Export already exists - download immediately
              await downloadWithDocuments(title);
            } else {
              // Job in progress or just started - show progress modal
              setExportProgressModalIsOpen(true);
              setIsExportCreating(true);
              setExportError(undefined);
            }
          }
        } catch (error) {
          console.error('Error starting chronology export:', error);
          setDownloadModalIsOpen(false);
          setExportError(error instanceof Error ? error.message : 'Export failed');
          setExportProgressModalIsOpen(true);
          setIsExportCreating(false);
        }
      } else {
        await fetchDownload({
          title,
          columns,
          styleFormat: StyleFormat.Table,
          downloadFormat,
          includeSourceDocuments: false,
        });
        setDownloadModalIsOpen(false);
      }
    },
    [fetchDownload, chronologyId, caseId],
  );

  const handleDownloadWord = useCallback(
    async ({
      title,
      style,
      downloadFormat,
      documentOptions,
      includeSourceDocuments,
    }: {
      title: string;
      downloadFormat: DownloadFormat;
      style?: string;
      documentOptions?: DocumentDownloadOptions;
      includeSourceDocuments?: boolean;
    }) => {
      trackCustomEvent(TrackingEvents.DownloadChronologyWrittenDocx, { chronologyId, caseId });
      if (style !== '') {
        trackCustomEvent(TrackingEvents.DownloadChronologyWrittenCustomStyle, { chronologyId, caseId });
      }

      if (includeSourceDocuments) {
        trackCustomEvent(TrackingEvents.DownloadChronologyWithDocuments, { chronologyId, caseId });

        try {
          const result = await fetchDownload({
            title,
            style,
            styleFormat: StyleFormat.Written,
            downloadFormat,
            documentOptions,
            includeSourceDocuments,
          });

          if (result) {
            setDownloadModalIsOpen(false);

            if (result.exportExists) {
              // Export already exists - download immediately
              await downloadWithDocuments(title);
            } else {
              // Job in progress or just started - show progress modal
              setExportProgressModalIsOpen(true);
              setIsExportCreating(true);
              setExportError(undefined);
            }
          }
        } catch (error) {
          console.error('Error starting chronology export:', error);
          setDownloadModalIsOpen(false);
          setExportError(error instanceof Error ? error.message : 'Export failed');
          setExportProgressModalIsOpen(true);
          setIsExportCreating(false);
        }
      } else {
        await fetchDownload({
          title,
          style,
          styleFormat: StyleFormat.Written,
          downloadFormat,
          documentOptions,
          includeSourceDocuments: false,
        });
        setDownloadModalIsOpen(false);
      }
    },
    [fetchDownload, chronologyId, caseId],
  );

  useEffect(() => {
    trackPageView(PageName.Chronology);
  }, []);

  // Pagination
  const goToPage = (val: number) => {
    const newPaginationState = { pageIndex: val, pageSize: pageSize };
    setPagination(newPaginationState);
  };
  const prevPage = () => goToPage(pageIndex - 1);
  const nextPage = () => goToPage(pageIndex + 1);
  const totalPages = responseChronologyFacts?.pageCount ?? 0;

  return (
    <>
      {chronologyId && (
        <div className="flex h-full flex-col">
          <ChronologyTableToolbar
            chronologyTitle={responseChronology?.title ?? ''}
            handleBack={handleBack}
            openModal={openModal}
            openDownloadModal={() => setDownloadModalIsOpen(true)}
            canGetPrevPage={pageIndex > 0}
            canGetNextPage={pageIndex < totalPages - 1}
            prevPage={prevPage}
            nextPage={nextPage}
            currentPage={pageIndex}
            noOfPages={totalPages}
            goToPage={goToPage}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
          />
          <TableWrapper>
            {isLoadingChronologyFacts ? (
              <div className="flex h-full w-full items-center justify-center">
                <FontAwesomeIcon icon={faCircleNotch} className="fa-spin" />
              </div>
            ) : responseChronologyFacts?.chronologyFacts && responseChronologyFacts?.chronologyFacts?.length > 0 ? (
              <div className="h-full">
                {viewMode === ViewMode.Table ? (
                  <ChronologyTimeline chronology={responseChronologyFacts?.chronologyFacts} />
                ) : (
                  <VisualTimeline chronology={responseChronologyFacts?.chronologyFacts} />
                )}
              </div>
            ) : null}
          </TableWrapper>
        </div>
      )}

      <Modal
        title={
          <div className="flex items-center gap-2 font-semibold text-blue-500">
            <FontAwesomeIcon icon={faPlusCircle} className="" />
            Add Fact
          </div>
        }
        isOpen={modalIsOpen}
        handleClose={closeModal}
        content={
          <ChronologyEventAdder
            handleNewFactCreated={handleNewFactCreated}
            chronologyId={chronologyId ?? ''}
            caseId={caseId ?? ''}
          />
        }
        size="small"
      />
      <DownloadModal
        modalTitle="Export Chronology"
        isOpen={downloadModalIsOpen}
        onClose={() => setDownloadModalIsOpen(false)}
        downloadTitle={responseChronology?.title ?? ''}
        isLoading={isLoadingDownload}
        columnOptions={chronologyDownloadColumnOptions}
        onDownloadTable={handleDownloadCSV}
        onDownloadWritten={handleDownloadWord}
        displayOptions={{
          showStyle: true,
          showDocumentOptions: false,
          showSourceDocumentsOption: true,
        }}
      />
      <ChronologyExportProgressModal
        isOpen={exportProgressModalIsOpen}
        onClose={() => {
          setExportProgressModalIsOpen(false);
          setIsExportCreating(false);
          setExportError(undefined);
        }}
        caseId={caseId ?? ''}
        chronologyId={chronologyId}
        chronologyTitle={responseChronology?.title ?? 'chronology'}
        onDownload={async () => {
          await downloadWithDocuments(responseChronology?.title ?? 'chronology');
        }}
        isCreating={isExportCreating}
        error={exportError}
      />
    </>
  );
};

export default ChronologyEditor;

const chronologyDownloadColumnOptions = [
  { label: 'Date (verbatim)', value: 'Date (verbatim)' },
  { label: 'Date (standardised)', value: 'Date (standardised)' },
  { label: 'Fact', value: 'Fact' },
  { label: 'Relevance', value: 'Relevance' },
  { label: 'Source Text', value: 'Source Text' },
  { label: 'Source Document', value: 'Source Document' },
  { label: 'Significance', value: 'Significance' },
  { label: 'Id', value: 'Id' },
  { label: 'Notes', value: 'Notes' },
  { label: 'Notes Date', value: 'Notes Date' },
  { label: 'Notes By', value: 'Notes By' },
  { label: 'Verified By', value: 'Verified By' },
  { label: 'Verified Date', value: 'Verified Date' },
];
